import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LogOut, Lock, Search, Download, Send, RefreshCw, FileText, Calendar as CalIcon, Edit3, Trash2, PlusCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import OfferManager from './OfferManager';
import SiteSettingsManager from './SiteSettingsManager';
import { EditBookingDialog, DeleteBookingDialog, ManualBookingDialog } from './BookingActionsDialogs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'osc_admin_token';

function statusBadgeClass(paymentStatus) {
  if (paymentStatus === 'PAID') return 'bg-green-500/15 text-green-400 border border-green-500/40';
  if (paymentStatus === 'FAILED') return 'bg-red-500/15 text-red-400 border border-red-500/40';
  return 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/40';
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [pw, setPw] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, revenue: 0 });
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [pkgFilter, setPkgFilter] = useState('ALL');
  const [paymentFilter, setPaymentFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [deleteBooking, setDeleteBooking] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const navigate = useNavigate();

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (paymentFilter !== 'ALL') params.payment_status = paymentFilter;
      if (pkgFilter !== 'ALL') params.package_id = pkgFilter;
      if (query) params.q = query;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [b, s] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeaders(), params }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      } else {
        toast({ title: 'Load failed', description: e?.message });
      }
    } finally { setLoading(false); }
  }, [statusFilter, paymentFilter, pkgFilter, query, dateFrom, dateTo]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const submit = async () => {
    setLoginLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthed(true);
      setPw('');
    } catch {
      toast({ title: 'ખોટો પાસવર્ડ' });
    } finally { setLoginLoading(false); }
  };

  const resendWA = async (id) => {
    try {
      await axios.post(`${API}/admin/bookings/${id}/resend-whatsapp`, {}, { headers: authHeaders() });
      toast({ title: 'WhatsApp queued' });
    } catch { toast({ title: 'Failed to queue' }); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${id}/document.pdf`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast({ title: 'PDF failed', description: e?.response?.data?.detail || e.message });
    }
  };

  const generateDoc = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/bookings/${id}/generate-doc`, {}, { headers: authHeaders() });
      toast({ title: `${res.data.doc_type || 'Doc'} generated`, description: res.data.doc_number || '' });
      load();
    } catch (e) {
      toast({ title: 'Generate failed', description: e?.response?.data?.detail || e.message });
    }
  };

  const backfillDocs = async () => {
    if (!window.confirm('Assign Invoice/Receipt numbers to ALL old PAID bookings that are missing them?\n\nSafe to run multiple times.')) return;
    try {
      const res = await axios.post(`${API}/admin/bookings/backfill-docs`, {}, { headers: authHeaders() });
      toast({ title: 'Backfill complete', description: `Updated ${res.data.updated} of ${res.data.scanned} bookings` });
      load();
    } catch (e) {
      toast({ title: 'Backfill failed', description: e?.response?.data?.detail || e.message });
    }
  };

  const exportCSV = () => {
    const rows = [['ID','Name','Mobile','Car','Package','Date','Amount','Payment Type','Payment Status','Created']];
    bookings.forEach(b => rows.push([b.id,b.name,b.mobile,b.car_model,b.package_name,new Date(b.booking_date).toLocaleDateString(),b.amount,b.payment_type,b.payment_status,new Date(b.created_at).toLocaleString()]));
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `bookings-${Date.now()}.csv`; a.click();
  };

  const filtered = useMemo(() => bookings, [bookings]);

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="card-dark w-full max-w-md p-8">
          <div className="flex items-center gap-3 mb-6"><Lock className="w-5 h-5 text-pink-400" /><h1 className="text-xl font-bold text-white">Admin Login</h1></div>
          <Label className="text-gray-300">Password</Label>
          <Input type="password" value={pw} onChange={e => setPw(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white mt-2" onKeyDown={e => e.key === 'Enter' && submit()} />
          <Button onClick={submit} disabled={loginLoading} className="gradient-btn w-full h-11 rounded-full mt-5 font-semibold">{loginLoading ? 'Logging in...' : 'Login'}</Button>
          <button onClick={() => navigate('/')} className="mt-4 text-sm text-gray-400 hover:text-white">← Back to site</button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white">Bookings <span className="gradient-text">Dashboard</span></h1>
            <p className="text-sm text-gray-400">One Stop Carz Admin</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setManualOpen(true)} data-testid="manual-booking-btn" className="gradient-btn rounded-full h-10 px-4 font-semibold text-sm"><PlusCircle className="w-4 h-4 mr-1" /> Manual Booking</Button>
            <Button onClick={load} variant="outline" className="border-purple-900/60 text-gray-200 hover:bg-purple-950/30"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <Button onClick={backfillDocs} data-testid="backfill-docs-btn" variant="outline" title="Assign missing Invoice/Receipt numbers to old paid bookings" className="border-purple-900/60 text-gray-200 hover:bg-purple-950/30"><FileText className="w-4 h-4 mr-1" /> Backfill Docs</Button>
            <Button onClick={exportCSV} variant="outline" className="border-purple-900/60 text-gray-200 hover:bg-purple-950/30"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
            <Button onClick={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} variant="outline" className="border-purple-900/60 text-gray-200 hover:bg-purple-950/30"><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            {label:'Total', v: stats.total, color:'from-purple-500 to-pink-500'},
            {label:'Paid', v: stats.confirmed, color:'from-green-500 to-emerald-500'},
            {label:'Pending', v: stats.pending, color:'from-yellow-500 to-orange-500'},
            {label:'Revenue', v:`₹${(stats.revenue || 0).toLocaleString()}`, color:'from-pink-500 to-rose-500'},
          ].map((s,i) => (
            <Card key={i} className="card-dark p-4">
              <div className="text-xs text-gray-400">{s.label}</div>
              <div className={`text-2xl font-extrabold mt-1 bg-gradient-to-r ${s.color} bg-clip-text text-transparent`}>{s.v}</div>
            </Card>
          ))}
        </div>

        <Card className="card-dark p-4 mb-4">
          <div className="grid md:grid-cols-6 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name, mobile, car, ID, invoice#..." className="bg-[#14141c] border-purple-900/60 text-white pl-9" />
            </div>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="bg-[#14141c] border-purple-900/60 text-white"><SelectValue placeholder="Payment" /></SelectTrigger>
              <SelectContent className="bg-[#14141c] border-purple-900/60 text-white">
                <SelectItem value="ALL">All Payments</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={pkgFilter} onValueChange={setPkgFilter}>
              <SelectTrigger className="bg-[#14141c] border-purple-900/60 text-white"><SelectValue placeholder="Package" /></SelectTrigger>
              <SelectContent className="bg-[#14141c] border-purple-900/60 text-white">
                <SelectItem value="ALL">All Packages</SelectItem>
                <SelectItem value="diamond">Diamond AMC</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="luxury">Luxury</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <CalIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" title="From date" className="bg-[#14141c] border-purple-900/60 text-white pl-9" />
            </div>
            <div className="relative">
              <CalIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" title="To date" className="bg-[#14141c] border-purple-900/60 text-white pl-9" />
            </div>
          </div>
          {(dateFrom || dateTo || paymentFilter !== 'ALL' || pkgFilter !== 'ALL' || query) && (
            <button onClick={() => { setQuery(''); setPaymentFilter('ALL'); setPkgFilter('ALL'); setDateFrom(''); setDateTo(''); }} className="text-xs text-pink-400 hover:text-pink-300 mt-2">Clear filters</button>
          )}
        </Card>

        <Card className="card-dark p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-purple-950/20 text-gray-300">
                <tr>
                  {['ID','Invoice/Receipt','Name','Mobile','Car','Package','Date','Amount','Pay Type','Payment','Actions'].map(h => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-10 text-center text-gray-400">{loading ? 'Loading...' : 'No bookings found'}</td></tr>
                )}
                {filtered.map(b => (
                  <tr key={b.id} className="border-t border-purple-900/40 text-gray-200 hover:bg-purple-950/10">
                    <td className="px-4 py-3 font-mono text-xs">{b.id}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {b.doc_number ? (
                        <span className={`inline-block px-2 py-0.5 rounded ${b.doc_type === 'INVOICE' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40' : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'}`}>{b.doc_number}</span>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3">{b.name}</td>
                    <td className="px-4 py-3"><a href={`tel:+91${b.mobile}`} className="hover:text-pink-400">{b.mobile}</a></td>
                    <td className="px-4 py-3">{b.car_model}</td>
                    <td className="px-4 py-3">{b.package_name}</td>
                    <td className="px-4 py-3">{new Date(b.booking_date).toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric'})}</td>
                    <td className="px-4 py-3 font-semibold">₹{b.amount}</td>
                    <td className="px-4 py-3 uppercase text-xs">{b.payment_type}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusBadgeClass(b.payment_status)}`}>{b.payment_status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button title="Edit" onClick={() => setEditBooking(b)} className="text-blue-400 hover:text-blue-300">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button title="Resend WhatsApp" onClick={() => resendWA(b.id)} className="text-green-400 hover:text-green-300">
                          <Send className="w-4 h-4" />
                        </button>
                        {b.payment_status === 'PAID' && (
                          <button title={b.doc_number ? `Download ${b.doc_type}` : 'Generate & Download PDF'} onClick={() => downloadPdf(b.id)} data-testid={`download-pdf-${b.id}`} className="text-pink-400 hover:text-pink-300 inline-flex items-center gap-1 text-xs">
                            <FileText className="w-4 h-4" />
                          </button>
                        )}
                        <button title="Delete (reason required)" onClick={() => setDeleteBooking(b)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <OfferManager />
        <SiteSettingsManager />

        <EditBookingDialog booking={editBooking} open={!!editBooking} onOpenChange={(v) => !v && setEditBooking(null)} onSaved={load} />
        <DeleteBookingDialog booking={deleteBooking} open={!!deleteBooking} onOpenChange={(v) => !v && setDeleteBooking(null)} onDeleted={load} />
        <ManualBookingDialog open={manualOpen} onOpenChange={setManualOpen} onCreated={load} />
      </div>
    </div>
  );
}
